import { TArchiveLearnerResDto } from "@/api/services/main/learner/dtos/archive-learner.dto";
import { TIssueAccountResDto } from "@/api/services/main/learner/dtos/issue-account.dto";
import { TSuspendAccountResDto } from "@/api/services/main/learner/dtos/suspend-account.dto";
import { TCreateLearnerPlanReqDto, TCreateLearnerReqDto, TCreateLearnerResDto, TUpdateLearnerReqDto } from "@/api/services/main/learner/dtos/create-learner-req.dto";
import { TGetLearnerListReqDto, TGetLearnerListResDto } from "@/api/services/main/learner/dtos/get-learner-list.dto";
import { TGetLearnerResDto } from "@/api/services/main/learner/dtos/get-learner.dto";
import { TUpdateLearnerPlanStatusReqDto } from "@/api/services/main/learner/dtos/update-learner-plan-status.dto";
import { TGetLearnerPlanListReqDto, TGetLearnerPlanListResDto } from "@/api/services/main/learner/dtos/get-learner-plan-list.dto";
import { TPrepareBulkImportReqDto, TPrepareBulkImportResDto, TPrepareBulkImportRow } from "@/api/services/main/learner/dtos/prepare-bulk-import.dto";
import { TBulkImportLearnerOutputDto, TBulkImportLearnerReqDto, TBulkImportLearnerResDto } from "@/api/services/main/learner/dtos/bulk-import-learner.dto";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { clientApi } from "@/utils/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { useMemo } from "react";

export const getLearnerListApi = async (params: TGetLearnerListReqDto): Promise<TGetLearnerListResDto> => {
  const { data } = await clientApi.get<TGetLearnerListResDto>("/learners", { params });
  return data;
};

export const useLearnerList = (params: TGetLearnerListReqDto) => {
  return useQuery({
    queryKey: [QUERY_KEYS.LEARNER.FETCH_LIST, params],
    queryFn: () => getLearnerListApi(params),
  });
};

export const getLearnerApi = async (id: string | number): Promise<TGetLearnerResDto> => {
  const { data } = await clientApi.get<TGetLearnerResDto>(`/learner/${id}`);
  return data;
};

export const useLearnerInfo = (id?: number) => {
  const { learnerId } = useParams();

  const fetchId = id ?? learnerId;

  const isValidId = useMemo(() => {
    if (!fetchId) return false;
    const idNumber = Number(fetchId);
    return !isNaN(idNumber) && Number.isInteger(idNumber) && idNumber > 0;
  }, [fetchId]);

  return useQuery({
    queryKey: [QUERY_KEYS.LEARNER.FETCH_DETAIL, fetchId],
    queryFn: async () => {
      return fetchId ? await getLearnerApi(fetchId) : undefined;
    },
    enabled: isValidId,
  });
};

// 一括取り込み: 選択された visa 従業員を study マスタへ突合し、ID解決済みの準備行を返す。RSO/Company 共通 (BEが identity で判定)。
export const prepareBulkImportApi = async (payload: TPrepareBulkImportReqDto): Promise<TPrepareBulkImportRow[]> => {
  const { data } = await clientApi.post<TPrepareBulkImportResDto>("/learner/bulk-import/prepare", payload);
  return data.data;
};

// 一括取り込み確定保存: 準備行(編集済み)を learner として一括作成し、新規所属機関も名称から作成する。
export const bulkImportLearnersApi = async (payload: TBulkImportLearnerReqDto): Promise<TBulkImportLearnerOutputDto> => {
  const { data } = await clientApi.post<TBulkImportLearnerResDto>("/learner/bulk-import", payload);
  return data.data;
};

export const createLearnerApi = async (payload: TCreateLearnerReqDto): Promise<TCreateLearnerResDto> => {
  const { data } = await clientApi.post<TCreateLearnerResDto>("/learner", payload);
  return data;
};

export const updateLearnerApi = async (id: string | number, payload: TUpdateLearnerReqDto): Promise<TCreateLearnerResDto> => {
  const { data } = await clientApi.put<TCreateLearnerResDto>(`/learner/${id}`, payload);
  return data;
};

export const setLearnerArchive = async (id: string | number, isArchive: boolean = false): Promise<TArchiveLearnerResDto> => {
  const { data } = await clientApi.patch<TArchiveLearnerResDto>(`/learner/${id}/archive`, { isArchive });
  return data;
};

export const issueAccountApi = async (id: string | number): Promise<TIssueAccountResDto> => {
  const { data } = await clientApi.post<TIssueAccountResDto>(`/learner/${id}/issue-account`);
  return data;
};

export const resetPasswordApi = async (id: string | number): Promise<TIssueAccountResDto> => {
  const { data } = await clientApi.post<TIssueAccountResDto>(`/learner/${id}/reset-password`);
  return data;
};

export const suspendAccountApi = async (id: string | number): Promise<TSuspendAccountResDto> => {
  const { data } = await clientApi.post<TSuspendAccountResDto>(`/learner/${id}/suspend-account`);
  return data;
};

export const createLearnerPlanApi = async (learnerId: string | number, payload: TCreateLearnerPlanReqDto): Promise<TCreateLearnerResDto> => {
  const { data } = await clientApi.post<TCreateLearnerResDto>(`/learner/${learnerId}/create-plan`, payload);
  return data;
};

export const getLearnerPlanListApi = async (
  learnerId: string | number,
  params: TGetLearnerPlanListReqDto,
): Promise<TGetLearnerPlanListResDto> => {
  const { data } = await clientApi.get<TGetLearnerPlanListResDto>(`/learner/${learnerId}/plans`, { params });
  return data;
};

export const useLearnerPlanList = (learnerId: string | number | undefined, params: TGetLearnerPlanListReqDto) => {
  return useQuery({
    queryKey: [QUERY_KEYS.LEARNER.FETCH_PLAN_LIST, learnerId, params],
    queryFn: () => getLearnerPlanListApi(learnerId!, params),
    enabled: !!learnerId,
    staleTime: Infinity,
    gcTime: Infinity,
  });
};

export const updateLearnerPlanStatusApi = async (
  params: TUpdateLearnerPlanStatusReqDto
): Promise<void> => {
  const { learnerId, planStatus } = params;
  await clientApi.patch(`/learner/${learnerId}/plan-status`, { planStatus });
};

export const useUpdateLearnerPlanStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: TUpdateLearnerPlanStatusReqDto) => updateLearnerPlanStatusApi(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.LEARNER.FETCH_LIST] });
    },
  });
};
